import { colorFor, initials } from '@hrms/shared'
import { useState } from 'react'
import { cn } from '../../lib/cn.ts'

export interface AvatarProps {
  /** Display name or email. Omit (or pass null) for the "Unassigned" variant. */
  name?: string | null
  /** Stable seed for the colour; defaults to the name. */
  seed?: string
  /** `xs` 16px (filter chips), `md` 24px, `lg` 32px. */
  size?: 'xs' | 'md' | 'lg'
  /** Profile picture (e.g. from Google); falls back to initials if it fails to load. */
  src?: string | null
  className?: string
}

export function Avatar({ name, seed, size = 'md', src, className }: AvatarProps) {
  const dims =
    size === 'lg'
      ? 'size-8 text-xs'
      : size === 'xs'
        ? 'size-4 text-[7.5px]'
        : 'size-6 text-[10.5px]'
  const [broken, setBroken] = useState<string | null>(null)
  if (name && src && broken !== src) {
    return (
      <img
        src={src}
        alt={name}
        referrerPolicy="no-referrer"
        onError={() => setBroken(src)}
        className={cn('inline-block shrink-0 rounded-full object-cover', dims, className)}
      />
    )
  }
  if (!name) {
    return (
      <span
        role="img"
        aria-label="Unassigned"
        className={cn(
          'inline-block shrink-0 rounded-full border border-dashed border-border-strong',
          dims,
          className,
        )}
      />
    )
  }
  return (
    <span
      role="img"
      aria-label={name}
      data-color={colorFor(seed ?? name)}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full bg-cb leading-none font-bold text-cf select-none',
        dims,
        className,
      )}
    >
      {initials(name)}
    </span>
  )
}
